/**
 * The single mobile navigation: a left panel whose rows expand in place.
 *
 * WHY THIS REPLACED THREE THINGS
 * The phone had accumulated a hamburger drawer (a flat section list with a
 * disconnected sub-page list stapled underneath), an on-page chip strip for the
 * current section's sub-pages, AND a bottom tab bar with a "More" sheet. Three
 * navigations, overlapping, none of them complete — the classic top-left drawer
 * plus a second bar at the bottom of the page.
 *
 * This is one panel behaving the way a website's left nav does: a section with
 * sub-pages expands to reveal them, a section without them navigates straight
 * away. Nothing is hidden behind a second surface.
 *
 * Behaviour:
 *   - The section containing the current route starts expanded, so opening the
 *     panel always shows where you are and what is next to it.
 *   - A section WITH sub-pages: the first tap only EXPANDS it — it does not
 *     navigate. Tapping it again navigates to its first page. This is what a
 *     website's left nav does, and it means a tap can never take you somewhere
 *     you were only trying to look inside.
 *   - A section WITHOUT sub-pages navigates immediately, on the first tap.
 */
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { DownSmallLine } from '@teivor/shared-ui';

import { findActiveSection, type NavNode } from '../lib/nav';

export function NavAccordion({
  nodes,
  pathname,
  onNavigate,
}: {
  nodes: NavNode[];
  pathname: string;
  /** Called after a navigation so the host can close the drawer. */
  onNavigate?: () => void;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const activeSection = findActiveSection(nodes, pathname);
  const [expandedId, setExpandedId] = React.useState<string | null>(activeSection?.id ?? null);

  // Follow the route: navigating from elsewhere (a link, the logo, back) should
  // leave the panel showing the section you ended up in, not the last one tapped.
  React.useEffect(() => {
    if (activeSection?.id) setExpandedId(activeSection.id);
  }, [activeSection?.id]);

  const go = (to: string) => {
    navigate(to);
    onNavigate?.();
  };

  // Most-specific match wins: items share prefixes (/shell/lead-pool is the
  // parent of /shell/lead-pool/history), so a plain startsWith lights up both.
  const activeItemTo = (node: NavNode) =>
    [...node.items]
      .filter((it) => pathname === it.to || pathname.startsWith(`${it.to}/`))
      .sort((a, b) => b.to.length - a.to.length)[0]?.to;

  return (
    <nav aria-label={t('nav.sections', 'Sekce')} className="flex flex-col gap-0.5 p-2">
      {nodes.map((node) => {
        const isActiveSection = node.id === activeSection?.id;
        const hasChildren = node.items.length > 1;
        const isExpanded = hasChildren && node.id === expandedId;
        const Icon = isActiveSection ? node.glyph.fill : node.glyph.line;
        const first = node.items[0]?.to ?? '/';
        const currentItem = activeItemTo(node);

        return (
          <div key={node.id}>
            <button
              type="button"
              aria-expanded={hasChildren ? isExpanded : undefined}
              aria-current={isActiveSection && !hasChildren ? 'page' : undefined}
              onClick={() => {
                // No sub-pages: nothing to reveal, so go straight there.
                if (!hasChildren) {
                  go(first);
                  return;
                }
                // First tap reveals the sub-pages; a second tap on the same row
                // is the one that navigates. Looking inside a section must never
                // move you out of the page you are on.
                if (!isExpanded) {
                  setExpandedId(node.id);
                  return;
                }
                go(first);
              }}
              className={[
                'flex w-full min-h-11 items-center gap-3 rounded-[var(--tvl-r2)] px-3 py-2 text-left',
                'text-[15px] leading-[20px] transition-colors',
                isActiveSection
                  ? 'bg-[var(--tvl-selected)] font-[500] text-[var(--tvl-ink)]'
                  : 'text-[var(--tvl-ink-2)] active:bg-[var(--tvl-wash-pressed)]',
              ].join(' ')}
            >
              <Icon size={20} color="currentColor" aria-hidden />
              {/* No truncate: a long Czech label wraps to a second line rather
                  than being silently cut with an ellipsis. There is vertical
                  room in a scrolling panel; there is not horizontal room. */}
              <span className="min-w-0 flex-1 break-words">{t(node.labelKey)}</span>
              {hasChildren ? (
                <DownSmallLine
                  size={18}
                  aria-hidden
                  className={[
                    'shrink-0 text-[var(--tvl-ink-3)] transition-transform',
                    isExpanded ? 'rotate-180' : '',
                  ].join(' ')}
                />
              ) : null}
            </button>

            {isExpanded ? (
              <ul className="mb-1 ml-[1.65rem] flex flex-col gap-0.5 border-l border-[var(--tvl-hairline)] pl-2">
                {node.items.map((item) => {
                  const isCurrent = item.to === currentItem;
                  return (
                    <li key={item.to}>
                      <button
                        type="button"
                        onClick={() => go(item.to)}
                        aria-current={isCurrent ? 'page' : undefined}
                        className={[
                          'flex w-full min-h-11 items-center rounded-[var(--tvl-r2)] px-3 py-2 text-left',
                          'text-[14px] leading-[20px] transition-colors',
                          isCurrent
                            ? 'bg-[var(--tvl-selected)] font-[500] text-[var(--tvl-ink)]'
                            : 'text-[var(--tvl-ink-2)] active:bg-[var(--tvl-wash-pressed)]',
                        ].join(' ')}
                      >
                        <span className="min-w-0 flex-1 break-words">{t(item.labelKey)}</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            ) : null}
          </div>
        );
      })}
    </nav>
  );
}
